import React, { useRef, useEffect, useCallback, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { debounce } from "lodash";
import * as settingsActions from "../app/modules/Estore/_redux/Estore/basicSettingsAction";
import { useDispatch } from "react-redux";
import { Spin } from "antd";
import { hasRestrictivePermission, Permission } from "../app/modules/Common/constants";

const isCostColumn = (column = {}) => {
  const columnText = [column.field, column.colId, column.dataIndex, column.headerName, column.title].filter(Boolean).join(" ");
  return /\bcost\b|_cost\b|\bcost_|extended_cost|ext_cst|pr_cost|po_cost|unit_cost|avg_cost|average_cost/i.test(columnText);
};

export default function CommonAgGridTable({
  rowData = [],
  columnDefs = [],
  pinnedCount = 0,
  onGridReady,
  selectedColumns = [],
  setSelectedColumns,
  setIsDragged,
  setPinnedCount,
  isUserAction,
  setIsUserAction,
  listLoading,
  type,
  subType,
  validateColumns,
  useKeyedColumnStore = false,
  style = { height: "950px", width: "100%" },
  pinnedMaxWidth = 430,
  rowSelectionEnabled = false,
  onRowSelectionChange,
  selectedRowKeys = [],
  getRowId,
  // Key used for `selectedRowKeys` / `onRowSelectionChange`, when it should differ from `getRowId`.
  // Pass one whenever getRowId embeds data that changes over time (updated_at, page position, ...),
  // otherwise a persisted selection stops matching its row and turns into a hidden selection.
  getSelectionKey,
  getRowStyle,
  pinnedBottomRowData,
  isRowSelectable,
  onSortChanged,
  rowHeight,
  headerHeight,
  defaultColDefOverrides,
  setRefresh = () => {},
  handleAddClick = () => {},
  activeTransactionTab = () => {},
  setActiveTransactionTab = () => {},
  handleUndoExpense = () => {},
  contextMenuItems = null,
  suppressContextMenu = false,
  enableCellTextSelection = !suppressContextMenu,
  gridContext = {},
  overlayNoRowsComponent,
  showTopScrollbar = true,
  suppressHorizontalScroll = false,
}) {
  const hideCostPrices = hasRestrictivePermission(Permission.HideCostPrices);
  const visibleColumnDefs = useMemo(() => (hideCostPrices ? columnDefs.filter((column) => !isCostColumn(column)) : columnDefs), [columnDefs, hideCostPrices]);

  const getKey = getSelectionKey || getRowId;
  const applySelectedRowKeys = (api) => {
    const selectedKeySet = new Set((selectedRowKeys || []).map(String));
    api.forEachNode((node) => {
      const nodeId = getKey ? getKey({ data: node.data }) : node.data?.id;
      node.setSelected(selectedKeySet.has(String(nodeId)));
    });
  };

  useEffect(() => {
    if (gridApiRef.current) {
      applySelectedRowKeys(gridApiRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowData, selectedRowKeys]);

  const [contextMenu, setContextMenu] = React.useState(null);
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    window.addEventListener("click", handleClick);
    return () => window.removeEventListener("click", handleClick);
  }, []);

  const gridWrapperRef = useRef(null);
  const topScrollRef = useRef(null);
  const gridApiRef = useRef(null);
  const columnApiRef = useRef(null);

  const gridTaskCleanupsRef = useRef([]);
  const dispatch = useDispatch();

  const scheduleGridWork = useCallback((work, delay = 0) => {
    let innerTimer;
    const outerTimer = setTimeout(() => {
      innerTimer = setTimeout(() => {
        if (!gridApiRef.current && !columnApiRef.current) {
          return;
        }
        work();
      }, 0);
    }, delay);

    const cleanup = () => {
      clearTimeout(outerTimer);
      if (innerTimer) {
        clearTimeout(innerTimer);
      }
    };
    gridTaskCleanupsRef.current.push(cleanup);
    return cleanup;
  }, []);

  useEffect(
    () => () => {
      gridTaskCleanupsRef.current.forEach((cleanup) => cleanup());
      gridTaskCleanupsRef.current = [];
      gridApiRef.current = null;
      columnApiRef.current = null;
    },
    []
  );

  useEffect(() => {
    if (!gridApiRef.current) {
      return undefined;
    }

    return scheduleGridWork(() => {
      if (!gridApiRef.current) return;
      applySelectedRowKeys(gridApiRef.current);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getKey, rowData, scheduleGridWork, selectedRowKeys]);

  const updateTopScrollWidth = useCallback(() => {
    const scrollable = gridWrapperRef.current?.querySelector(".ag-body-horizontal-scroll-viewport");
    const leftPinned = gridWrapperRef.current?.querySelector(".ag-pinned-left-cols-container");
    const topScroll = topScrollRef.current;
    if (scrollable && topScroll) {
      const pinnedLeftWidth = leftPinned?.offsetWidth || 0;
      topScroll.firstChild.style.width = `${scrollable.scrollWidth}px`;
      topScroll.style.paddingLeft = `${pinnedLeftWidth}px`;
    }
  }, []);

  useEffect(() => {
    if (!gridApiRef.current) {
      return;
    }

    gridApiRef.current.refreshCells({ force: true });
    gridApiRef.current.redrawRows();
    updateTopScrollWidth();
  }, [rowData, updateTopScrollWidth]);

  const initTopScrollbar = useCallback(() => {
    const scrollable = gridWrapperRef.current?.querySelector(".ag-body-horizontal-scroll-viewport");
    const topScroll = topScrollRef.current;
    if (!scrollable || !topScroll) return;
    const syncToTop = (e) => (topScroll.scrollLeft = e.target.scrollLeft);
    const syncToGrid = (e) => (scrollable.scrollLeft = e.target.scrollLeft);
    scrollable.addEventListener("scroll", syncToTop);
    topScroll.addEventListener("scroll", syncToGrid);
    updateTopScrollWidth();
    window.addEventListener("resize", updateTopScrollWidth);
    return () => {
      scrollable.removeEventListener("scroll", syncToTop);
      topScroll.removeEventListener("scroll", syncToGrid);
      window.removeEventListener("resize", updateTopScrollWidth);
    };
  }, [updateTopScrollWidth]);

  const debouncedSaveColumnWidths = useMemo(
    () =>
      debounce((dispatch, updatedColumns, pinCount) => {
        const saveColumnListing = useKeyedColumnStore ? settingsActions.setDragColumnListingByKey : settingsActions.setDragColumnListing;
        dispatch(
          saveColumnListing(
            {
              type,
              sub_type: subType,
              force: 1,
              validate_columns: validateColumns,
            },
            {
              selectedColumns: updatedColumns,
              pinnedCount: pinCount,
            }
          )
        );
      }, 300),
    [subType, type, useKeyedColumnStore, validateColumns]
  );

  useEffect(() => {
    return () => debouncedSaveColumnWidths.cancel();
  }, [debouncedSaveColumnWidths]);

  const handleColumnResized = useCallback(
    (params) => {
      if (!params.finished || !params.column) return;

      const colId = params.column.getColId();
      const newWidth = params.column.getActualWidth();

      if (params.source === "uiColumnResized" && setSelectedColumns) {
        setSelectedColumns((prevSelectedColumns) => {
          const updated = prevSelectedColumns.map((col) => (col.dataIndex === colId ? { ...col, width: newWidth } : col));

          debouncedSaveColumnWidths(dispatch, updated, pinnedCount);

          return updated;
        });
      }
      updateTopScrollWidth();
    },
    [debouncedSaveColumnWidths, dispatch, pinnedCount, updateTopScrollWidth]
  );

  const handleColumnMoved = useCallback(
    (params) => {
      if (params.source !== "uiColumnMoved") return;

      updateTopScrollWidth();
      if (!columnApiRef.current) return;

      const allGridCols = columnApiRef.current.getAllGridColumns() || [];
      const updatedVisibleCols = allGridCols
        .map((col) => {
          const colId = col.getColId();
          if (colId === "checkbox") return null;
          const found = selectedColumns.find((c) => (c.dataIndex || c.field || c.colId) === colId);
          return found ? { ...found } : null;
        })
        .filter(Boolean);
      const hiddenCols = selectedColumns.filter((col) => {
        const colId = col.dataIndex || col.field || col.colId;
        return !col.checked && !updatedVisibleCols.some((visibleCol) => (visibleCol.dataIndex || visibleCol.field || visibleCol.colId) === colId);
      });
      const updatedCols = [...updatedVisibleCols, ...hiddenCols];

      const pinnedColumns = updatedCols.filter((col) => col.pinned);
      if (pinnedColumns.length > 3) {
        const lastPinnedColumn = pinnedColumns[pinnedColumns.length - 1];
        columnApiRef.current.getColumnState().forEach((state) => {
          if (state.colId === lastPinnedColumn.colId) {
            state.pinned = null; // Unpin the column
          }
        });
        scheduleGridWork(() => {
          if (!columnApiRef.current) return;
          columnApiRef.current.applyColumnState({
            state: columnApiRef.current.getColumnState(),
            applyOrder: true,
          });
        });
      }

      if (setSelectedColumns) setSelectedColumns(updatedCols);
      if (setIsDragged) setIsDragged(true);
    },
    [selectedColumns, pinnedCount, scheduleGridWork, setSelectedColumns, setIsDragged, updateTopScrollWidth]
  );

  const flexColumnIds = useMemo(() => {
    return new Set(
      visibleColumnDefs
        .filter((col) => col?.flex)
        .map((col) => col.colId || col.field || col.dataIndex)
        .filter(Boolean)
    );
  }, [visibleColumnDefs]);

  const applyColumnOrderAndPinning = useCallback(
    (columnsToApply, pinCount) => {
      const columnApi = columnApiRef.current;
      if (!columnApi || !columnsToApply?.length) return;

      const allColumns = columnApi.getAllGridColumns() || [];
      const hasCheckbox = allColumns.some((col) => col.getColId() === "checkbox");

      const pinState = [];
      if (hasCheckbox) {
        pinState.push({ colId: "checkbox", pinned: "left" });
      }

      let nonCheckboxIndex = 0;
      columnsToApply.forEach((col) => {
        const colId = col?.dataIndex || col?.field || col?.colId;
        if (!colId || colId === "checkbox") return;

        if (col.checked !== false) {
          const isPinned = nonCheckboxIndex < pinCount;
          pinState.push({
            colId,
            pinned: isPinned ? "left" : null,
            hide: false,
          });
          nonCheckboxIndex += 1;
        } else {
          pinState.push({
            colId,
            pinned: null,
            hide: true,
          });
        }
      });

      columnApi.applyColumnState({
        state: pinState,
        defaultState: { pinned: null },
        applyOrder: true,
      });

      updateTopScrollWidth();
    },
    [updateTopScrollWidth]
  );

  // AG-Grid permanently drops flex off a column the moment it's manually drag-resized,
  // even if its colDef still declares flex:1 (e.g. a width persisted from an earlier
  // session). Re-assert flex once right after the grid mounts so a stale resize doesn't
  // stop the designated flex column (usually the last visible one) from expanding to
  // fill the grid. This must NOT run on every column-state apply (pinning/reorder/column
  // selection) or it would immediately snap the column back to flex right after the user
  // drags to resize it, making that column feel unresizable.
  const reassertFlexOnMount = useCallback(() => {
    const columnApi = columnApiRef.current;
    if (!columnApi || !flexColumnIds.size) return;
    columnApi.applyColumnState({
      state: [...flexColumnIds].map((colId) => ({ colId, flex: 1 })),
    });
  }, [flexColumnIds]);

  const applyPinning = useCallback(
    (count, isUserAction = true) => {
      const columnApi = columnApiRef.current;
      if (!columnApi) return;

      applyColumnOrderAndPinning(selectedColumns, count);

      if (isUserAction) {
        debouncedSaveColumnWidths(dispatch, selectedColumns, count);
        setIsUserAction?.(false);
      }
      setPinnedCount?.(count);
      updateTopScrollWidth();
    },
    [applyColumnOrderAndPinning, debouncedSaveColumnWidths, dispatch, selectedColumns, setIsUserAction, setPinnedCount, updateTopScrollWidth]
  );

  const applyPersistedWidths = useCallback(() => {
    const columnApi = columnApiRef.current;
    if (!columnApi || !selectedColumns?.length) return;

    selectedColumns.forEach((col) => {
      const colId = col?.dataIndex || col?.field || col?.colId;
      if (colId && col?.width && !flexColumnIds.has(colId)) {
        columnApi.setColumnWidth(colId, Number(col.width), false);
      }
    });

    updateTopScrollWidth();
  }, [flexColumnIds, selectedColumns, updateTopScrollWidth]);

  useEffect(() => {
    if (columnApiRef.current && selectedColumns.length > 0) {
      return scheduleGridWork(() => {
        applyPersistedWidths();
        applyColumnOrderAndPinning(selectedColumns, pinnedCount);
        updateTopScrollWidth();
      });
    }

    return undefined;
  }, [pinnedCount, selectedColumns, applyPersistedWidths, applyColumnOrderAndPinning, scheduleGridWork, updateTopScrollWidth]);

  useEffect(() => {
    const gridElement = gridWrapperRef.current;
    if (!gridElement) return;

    const handleMouseOver = (e) => {
      if (e.target.classList.contains("ag-header-cell-resize")) {
        e.target.classList.add("highlight-line");
      }
    };

    const handleMouseOut = (e) => {
      if (e.target.classList.contains("ag-header-cell-resize")) {
        e.target.classList.remove("highlight-line");
      }
    };

    gridElement.addEventListener("mouseover", handleMouseOver);
    gridElement.addEventListener("mouseout", handleMouseOut);

    return () => {
      gridElement.removeEventListener("mouseover", handleMouseOver);
      gridElement.removeEventListener("mouseout", handleMouseOut);
    };
  }, []);

  const enhancedColumnDefs = useMemo(() => {
    let nonCheckboxColumnIndex = 0;
    return visibleColumnDefs.map((col) => {
      if (col.colId === "checkbox" || col.field === "checkbox") {
        return col;
      }

      const shouldLimitPinnedWidth = nonCheckboxColumnIndex < pinnedCount;
      nonCheckboxColumnIndex += 1;

      if (shouldLimitPinnedWidth) {
        return { ...col, maxWidth: pinnedMaxWidth };
      }
      return col;
    });
  }, [visibleColumnDefs, pinnedCount, pinnedMaxWidth]);

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: false,
      suppressMenu: false,
      wrapText: true,
      autoHeight: true,
      ...(defaultColDefOverrides || {}),
    }),
    [defaultColDefOverrides]
  );

  const rowIdProps = getRowId ? { getRowId } : {};

  return (
    <div style={{ position: "relative" }}>
      {showTopScrollbar ? (
        <div
          ref={topScrollRef}
          className="common-ag-grid-top-scrollbar"
          style={{
            position: "sticky",
            top: 0,
            height: 16,
            overflowX: "auto",
            overflowY: "hidden",
            background: "#fff",
          }}
        >
          <div style={{ height: 1 }} />
        </div>
      ) : null}

      <Spin spinning={listLoading} tip="Loading...">
        <div className="ag-theme-alpine" style={style} ref={gridWrapperRef} onContextMenu={(e) => e.preventDefault()}>
          <AgGridReact
            rowData={rowData}
            columnDefs={enhancedColumnDefs}
            defaultColDef={defaultColDef}
            rowHeight={rowHeight}
            headerHeight={headerHeight}
            enableCellTextSelection={enableCellTextSelection}
            suppressDragLeaveHidesColumns={true}
            suppressHorizontalScroll={suppressHorizontalScroll}
            onGridReady={(params) => {
              gridApiRef.current = params.api;
              columnApiRef.current = params.columnApi;
              // The selection effects above bail out while the grid API is missing, and nothing re-runs
              // them when it arrives - so rows that loaded before the grid was ready (e.g. a selection
              // restored from storage) would render unchecked. Apply it once here too.
              applySelectedRowKeys(params.api);
              scheduleGridWork(() => {
                applyPersistedWidths();
                applyPinning(pinnedCount, false);
                reassertFlexOnMount();
                initTopScrollbar();
              }, 100);
              if (onGridReady) {
                onGridReady(params.api, params.columnApi); // expose APIs to parent
              }
            }}
            {...rowIdProps}
            onColumnMoved={handleColumnMoved}
            onColumnResized={handleColumnResized}
            onSortChanged={onSortChanged}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            onSelectionChanged={(params) => {
              if (rowSelectionEnabled && onRowSelectionChange) {
                const selectedRows = params.api.getSelectedRows();
                // Use getSelectionKey/getRowId if provided, otherwise fallback to .id
                const selectedKeys = selectedRows
                  .map((row) => (getKey ? getKey({ data: row }) : row.id))
                  .filter((key) => key !== undefined && key !== null);
                onRowSelectionChange(selectedKeys, selectedRows);
              }
            }}
            getRowStyle={getRowStyle}
            pinnedBottomRowData={pinnedBottomRowData}
            isRowSelectable={isRowSelectable}
            context={{
              setRefresh,
              gridContext,
              //  activeTransactionTab, setActiveTransactionTab ,handleAddClick, handleUndoExpense
            }}
            suppressContextMenu={suppressContextMenu}
            noRowsOverlayComponent={overlayNoRowsComponent}
            onCellContextMenu={(params) => {
              if (!params.data || !contextMenuItems) return;

              params.event.preventDefault();

              const clickX = params.event.clientX;
              const clickY = params.event.clientY;

              const menuWidth = 200;
              const menuHeight = 40 * contextMenuItems(params.data).length;

              const screenWidth = window.innerWidth;
              const screenHeight = window.innerHeight;

              const adjustedX = clickX + menuWidth > screenWidth ? screenWidth - menuWidth - 10 : clickX;

              const adjustedY = clickY + menuHeight > screenHeight ? screenHeight - menuHeight - 10 : clickY;

              setContextMenu({
                x: adjustedX,
                y: adjustedY,
                rowData: params.data,
              });
            }}
          />
        </div>
      </Spin>
      {contextMenu && contextMenuItems && (
        <div
          onContextMenu={(e) => e.preventDefault()}
          style={{
            position: "fixed",
            top: contextMenu.y,
            left: contextMenu.x,
            background: "#fff",
            border: "1px solid #e8e8e8",
            boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
            zIndex: 9999,
            borderRadius: 6,
            width: 200,
            padding: "5px 0",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenuItems(contextMenu.rowData)?.map((item, index) => (
            <div
              key={index}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                fontSize: 14,
                color: item.danger ? "red" : "#333",
              }}
              onClick={() => {
                item.onClick?.(contextMenu.rowData);
                setContextMenu(null);
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {item.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
